export interface Empleado {
  empleadoId: number;
  empresaId: number;
  nombreEmpresa: string;
  negocioId: number;
  nombreNegocio: string;
  nombreEmpleado: string;
  especialidad: string | null;
  calificacion: number | null;
  aniosExperiencia: number | null;
  imagenUrl: string | null;
  disponible: boolean;
  notas: string | null;
  creadoPor: string | null;
  fechaCreacion: string;
}

export interface CreateEmpleadoDto {
  empresaId: number;
  negocioId: number;
  nombreEmpleado: string;
  especialidad: string | null;
  calificacion: number | null;
  aniosExperiencia: number | null;
  imagenUrl: string | null;
  disponible: boolean;
  notas: string | null;
  creadoPor: string | null;
}

export interface UpdateEmpleadoDto {
  empresaId: number;
  negocioId: number;
  nombreEmpleado: string;
  especialidad: string | null;
  calificacion: number | null;
  aniosExperiencia: number | null;
  imagenUrl: string | null;
  disponible: boolean;
  notas: string | null;
}

class EmpleadosService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7123/api';
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error en la conexión con el servidor',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    if (response.status === 204) {
      return {} as T;
    }

    return response.json();
  }

  async getAll(): Promise<Empleado[]> {
    return this.request<Empleado[]>('/empleados');
  }

  async getById(id: number): Promise<Empleado> {
    return this.request<Empleado>(`/empleados/${id}`);
  }

  async create(data: CreateEmpleadoDto): Promise<Empleado> {
    return this.request<Empleado>('/empleados', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async update(id: number, data: UpdateEmpleadoDto): Promise<Empleado> {
    return this.request<Empleado>(`/empleados/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(id: number): Promise<void> {
    await this.request<void>(`/empleados/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadImage(id: number, file: File): Promise<{ imagenUrl: string }> {
    const url = `${this.baseUrl}/empleados/${id}/upload-imagen`;
    const formData = new FormData();
    formData.append('file', file);

    const headers: Record<string, string> = {};
    const token = localStorage.getItem('authToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: 'Error al subir la imagen',
      }));
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    return response.json();
  }
}

export const empleadosService = new EmpleadosService();
